// Data hooks of the meeting screens (COM-FE-05, COMX-FE-02/03): the meeting (kept fresh by
// `meeting.updated`), the lists of meetings of a space, a channel and an organisation (kept fresh
// by the same event), and thin wrappers on the generated mutations.
import {
  getMeetingsControllerGetQueryKey,
  useChannelMeetingsControllerList,
  useMeetingsControllerGet,
  useMeetingsControllerInvite,
  useMeetingsControllerInviteMembers,
  useMembersControllerList,
  useOrgCallsControllerStart,
  useOrgMeetingsControllerList,
  useSpaceMeetingsControllerCreate,
  useSpaceMeetingsControllerList,
  type MeetingListDtoOutputItemsItem,
  type OrgMeetingsControllerListParams,
  type SpaceMeetingsControllerListParams,
} from '@batiflow/api-client';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useRealtime, useRealtimeRoom } from '@/features/realtime';

/** One line of a list of meetings (a meeting or a call, with its minutes reference). */
export type MeetingSummary = MeetingListDtoOutputItemsItem;

/** The members the organisation's people picker offers (active ones only). */
const PICKER_SIZE = 200;

/** The meeting: its status and who is in the room now. Not retried: a 403 or 404 is the answer. */
export function useMeeting(meetingId: string, enabled = true) {
  return useMeetingsControllerGet(meetingId, { query: { enabled, retry: false } });
}

/**
 * Takes the room `meeting:{id}` for as long as the screen is open and refetches the meeting when
 * `meeting.updated` arrives (someone joined or left, the meeting started or ended).
 */
export function useMeetingEvents(meetingId: string, enabled = true): void {
  const client = useRealtime();
  const queryClient = useQueryClient();
  const fragment = `/meetings/${meetingId}`;
  useRealtimeRoom(enabled ? `meeting:${meetingId}` : null, { fragments: [fragment] });
  useEffect(() => {
    if (!client || !enabled) return;
    return client.on('meeting.updated', ({ data }) => {
      if (data.meetingId !== meetingId) return;
      void queryClient.invalidateQueries({ queryKey: getMeetingsControllerGetQueryKey(meetingId) });
    });
  }, [client, enabled, meetingId, queryClient]);
}

/** The list endpoints (`/spaces/{id}/meetings`, `/channels/{id}/meetings`, `/orgs/{id}/meetings`). */
const LIST_KEY = /^\/api\/v1\/(spaces|channels|orgs)\/[^/]+\/meetings$/;

/**
 * Keeps the lists of meetings on screen fresh: any `meeting.updated` (a meeting started, someone
 * joined, it ended) refetches the lists that are mounted. Mount it once per screen that shows a
 * list.
 */
export function useMeetingListEvents(enabled = true): void {
  const client = useRealtime();
  const queryClient = useQueryClient();
  useEffect(() => {
    if (!client || !enabled) return;
    return client.on('meeting.updated', () => {
      void queryClient.invalidateQueries({
        predicate: (query) => {
          const head: unknown = query.queryKey[0];
          return typeof head === 'string' && LIST_KEY.test(head);
        },
      });
    });
  }, [client, enabled, queryClient]);
}

/** The meetings of a space (project or shared), filtered. */
export function useSpaceMeetings(
  spaceId: string,
  params: SpaceMeetingsControllerListParams,
  enabled = true,
) {
  return useSpaceMeetingsControllerList(spaceId, params, { query: { enabled, retry: false } });
}

/** The meetings and calls held in a channel. A failure is silent: the messages still show. */
export function useChannelMeetings(channelId: string, enabled = true) {
  return useChannelMeetingsControllerList(
    channelId,
    { limit: 50 },
    { query: { enabled, retry: false } },
  );
}

/** The meetings of the organisation the person may join (the calendar's "Réunions" view). */
export function useOrgMeetings(
  orgId: string,
  params: OrgMeetingsControllerListParams,
  enabled = true,
) {
  return useOrgMeetingsControllerList(orgId, params, { query: { enabled, retry: false } });
}

/** The members of the organisation: who "Personnes" can invite. */
export function useOrgMembers(orgId: string, enabled = true) {
  return useMembersControllerList(
    orgId,
    { limit: PICKER_SIZE, status: 'active' },
    { query: { enabled } },
  );
}

export {
  useMeetingsControllerInvite as useInviteGuest,
  useMeetingsControllerInviteMembers as useInviteMembers,
  useOrgCallsControllerStart as useStartCall,
  useSpaceMeetingsControllerCreate as useCreateMeeting,
};
